import React, { useState, useEffect } from 'react'
import {
    TextField,
    Typography,
    FormControl,
    Select,
    MenuItem,
} from '@mui/material'
import LoadingDefault from '@/Components/Elements/Loading/LoadingDefault'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPaperPlane } from '@fortawesome/pro-duotone-svg-icons/faPaperPlane'
import { faChevronDown } from '@fortawesome/pro-duotone-svg-icons/faChevronDown'
import { faSpinner } from '@fortawesome/pro-duotone-svg-icons/faSpinner'
import { Editor } from '@tinymce/tinymce-react'
import SelectRC, { Option } from 'rc-select'
import ConfirmDeleteChangesDialog from '@/Components/Form/ConfirmDeleteChangesDialog'
import AppDialog from '@/Components/AppDialog'
import { StyledButton } from '@/Components/Elements/StyledComponents/Buttons'
import { ComposeMessageProps } from './types'

const ComposeMessage: React.FC<ComposeMessageProps> = (props) => {
    const sender: Array<any> = []
    if (props.to) {
        props.to.forEach((item) =>
            sender.push({
                id: item.id,
                value: item.id,
                label: item.sender_name,
            })
        )
    }

    const [limit] = useState(50)
    const [errors, setErrors] = useState<false | Record<string, string>>(false)
    const [sending, setSending] = useState(false)
    const [messageText, setMessageText] = useState<string | null>(null)
    const [messageTitle, setMessageTitle] = useState<string | null>(
        props.title ? 'RE: ' + props.title : null
    )
    const [messageType, setMessageType] = useState<string | null>('normal')
    const [showCancelAlert, setShowCancelAlert] = useState(false)
    const [showSuccessAlert, setShowSuccessAlert] = useState(false)
    const [to, setTo] = useState<Array<any>>(props.to ? sender : [])
    const [users] = useState<Array<any>>(props.to ? sender : [])
    const [options, setOptions] = useState<Array<any>>([])
    const [userLoading, setUserLoading] = useState(false)

    const kedo = props.kedo

    const sendMessage = () => {
        const userIds = to.map((val) => val.id)

        if (!messageText || !messageTitle || !to) {
            const errs: Record<string, string> = {}
            if (!to || to.length <= 0)
                errs.to = kedo.t('This is a required field')
            if (!messageTitle || messageTitle.length <= 0)
                errs.title = kedo.t('This is a required field')
            if (!messageText || messageText.length <= 0)
                errs.message = kedo.t('This is a required field')
            setErrors(errs)
            return
        }

        setErrors(false)
        setSending(true)

        const data: Record<string, any> = {
            messageText,
            title: messageTitle,
            users: userIds,
            dossier: props.dossier ? props.dossier.id : null,
        }

        if (messageType) data.type = messageType

        if (props.dossier) {
            data.contentDossier = props.dossier.id
            data.dossier = props.dossier.id
            data.environment = kedo.env().getEnvironment().id
        } else if (props.dossierId) {
            data.contentDossier = props.dossierId
            data.dossier = props.dossierId
            data.environment = kedo.env().getEnvironment().id
        }

        kedo.api()
            .post(kedo.api().getMessageEndpoint(), data)
            .then(() => {
                setErrors(false)
                setMessageText('')
                setMessageTitle('')
                setMessageType(null)
                setShowSuccessAlert(true)
                setSending(false)
                setTo([])
                props.onCloseModal()
            })
            .catch((error: any) => {
                setSending(false)
                setErrors(
                    error.response &&
                        error.response.data &&
                        error.response.data.errors
                        ? error.response.data.errors
                        : true
                )
            })
    }

    const getOptions = async (input: string) => {
        const params: any = { params: { limit, active: 1 } }
        if (input.length > 0) params.params.name = input
        if (props.dossier && props.dossier.environment_id) {
            params.params.environment = props.dossier.environment_id
        }

        setUserLoading(true)

        return kedo
            .api()
            .get(kedo.api().getUserEndpoint(), params)
            .then((response: any) => {
                if (!response.data.results) return []

                const newOptions = response.data.results.map((user: any) => ({
                    id: user.id,
                    value: user.id,
                    label: user.profile
                        ? `${user.profile.last_name}, ${user.profile.first_name}`
                        : user.username,
                }))

                setOptions((prevOptions) => [
                    ...prevOptions,
                    ...newOptions.filter(
                        (newOption: any) =>
                            !prevOptions.some(
                                (option: any) => option.id === newOption.id
                            )
                    ),
                ])
            })
            .finally(() => setUserLoading(false))
    }

    const handleSearch = (value: string) => getOptions(value)

    const handleReceiversChange = (selectedItems: any) => {
        if (!selectedItems || !selectedItems.map) {
            setTo([])
            return
        }
        setTo(selectedItems)
    }

    const renderUsers = () => {
        const filteredOptions = options.filter(
            (option: any) =>
                !to.some((selected) => selected.value === option.value)
        )

        return (
            <div>
                <SelectRC
                    className={`w-100 custom-select-react ${
                        errors && errors.to ? 'border-red is-invalid' : ''
                    }`}
                    loading={userLoading}
                    onSearch={handleSearch}
                    mode="multiple"
                    placeholder={kedo.t('Search') + '...'}
                    value={to.filter((option: any) =>
                        options.some((item) => item.value === option.value)
                    )}
                    onChange={(selectedItems: any) => {
                        const selectedOptions = selectedItems
                            .map((selectedItem: any) =>
                                options.find(
                                    (option: any) =>
                                        option.value === selectedItem
                                )
                            )
                            .filter(Boolean)
                        handleReceiversChange(selectedOptions)
                    }}
                    filterOption={(input, option: any) =>
                        option.props.children
                            .toLowerCase()
                            .includes(input.toLowerCase())
                    }
                    suffixIcon={
                        <FontAwesomeIcon
                            spin={userLoading}
                            icon={userLoading ? faSpinner : faChevronDown}
                        />
                    }
                    notFoundContent={kedo.t('No items found')}
                >
                    {filteredOptions.map((option: any) => (
                        <Option key={option.id} value={option.value}>
                            {option.label}
                        </Option>
                    ))}
                </SelectRC>
                {errors && errors.to && (
                    <Typography color="error" variant="caption">
                        {errors.to}
                    </Typography>
                )}
            </div>
        )
    }

    const renderCancelAlert = () => {
        if (!showCancelAlert || messageText === null) return null
        return (
            <ConfirmDeleteChangesDialog
                kedo={kedo}
                variant="modal"
                accept={props.onCloseModal}
                cancel={() => setShowCancelAlert(false)}
            />
        )
    }

    const checkDefaultMessage = () => {
        if (props.dossier) {
            // TODO: keep same logic if needed
        }
        return false
    }

    const handleCancel = () => {
        setShowSuccessAlert(false)
        if (messageText === null) props.onCloseModal()
        else setShowCancelAlert(true)
    }

    const onChangeMessageText = (content: string) => setMessageText(content)

    useEffect(() => {
        getOptions('')
    }, [])

    return (
        <div>
            {showCancelAlert && renderCancelAlert()}
            <AppDialog
                open={true}
                topAlign={true}
                onClose={
                    messageText === null || showCancelAlert
                        ? props.onCloseModal
                        : handleCancel
                }
                maxWidth="md"
                fullWidth
                title={kedo.t('Compose message')}
                actions={
                    <>
                        <StyledButton
                            onClick={sendMessage}
                            startIcon={
                                sending ? (
                                    <LoadingDefault size="sm" as="span" />
                                ) : (
                                    <FontAwesomeIcon icon={faPaperPlane} />
                                )
                            }
                        >
                            {kedo.t('Send message')}
                        </StyledButton>
                        <StyledButton onClick={handleCancel} color="secondary">
                            {kedo.t('Cancel')}
                        </StyledButton>
                    </>
                }
            >
                <>
                    <FormControl fullWidth margin="normal">
                        <Typography fontWeight="bold">
                            {kedo.t('To')}
                        </Typography>
                        {renderUsers()}
                    </FormControl>

                    <FormControl fullWidth margin="normal">
                        <Typography fontWeight="bold">
                            {kedo.t('Title')}
                        </Typography>
                        <TextField
                            size="small"
                            variant="outlined"
                            value={messageTitle || ''}
                            onChange={(e) => setMessageTitle(e.target.value)}
                            error={!!(errors && errors.title)}
                            helperText={
                                errors && errors.title ? errors.title : ''
                            }
                        />
                    </FormControl>

                    {(kedo.isAdminOrEnvironmentAdmin() ||
                        kedo.user().isContractAdminUser()) && (
                        <FormControl fullWidth margin="normal">
                            <Typography fontWeight="bold">
                                {kedo.t('Type')}
                            </Typography>
                            <Select
                                size="small"
                                value={messageType || ''}
                                onChange={(e) => setMessageType(e.target.value)}
                            >
                                <MenuItem value="normal">
                                    {kedo.t('Default message')}
                                </MenuItem>
                                <MenuItem value="popup">
                                    {kedo.t('Pop-up message')}
                                </MenuItem>
                            </Select>
                        </FormControl>
                    )}

                    <FormControl fullWidth margin="normal">
                        <Typography fontWeight="bold">
                            {kedo.t('Message')}
                        </Typography>
                        <div
                            className={
                                errors && errors.message ? 'is-invalid' : ''
                            }
                        >
                            <Editor
                                initialValue={checkDefaultMessage()}
                                onEditorChange={onChangeMessageText}
                                value={
                                    checkDefaultMessage()
                                        ? checkDefaultMessage()
                                        : messageText || ''
                                }
                                init={{
                                    branding: false,
                                    default_link_target: '_blank',
                                    font_formats:
                                        'Arial=arial,helvetica,sans-serif; Arial Black=arial black,gadget,sans-serif; Comic Sans=comic sans ms,cursive,sans-serif; Courier New=courier new,courier,monospace; Georgia=georgia,serif; Impact=impact,charcoal,sans-serif; Lucida Sans Unicode=lucida sans unicode,lucida grande,sans-serif; Palatino Linotype=alatino linotype,book antiqua,palatino,serif; Tahoma=tahoma,geneva,sans-serif; Trebuchet=trebuchet,helvetica,sans-serif; Times New Roman=times new roman,times,serif; Verdana=verdana,geneva,sans-serif;',
                                    fontsize_formats:
                                        '8pt 10pt 12pt 14pt 16pt 18pt 24pt 36pt 48pt',
                                    height: 250,
                                    body_class: 'tinyMCE-editor',
                                    browser_spellcheck: true,
                                    contextmenu: false,
                                    plugins: [
                                        'table',
                                        'lists',
                                        'link',
                                        'autolink',
                                        'emoticons',
                                        'help',
                                    ],
                                    menubar: false,
                                    toolbar:
                                        'undo redo | styleselect | fontselect fontsizeselect bold italic underline strikethrough forecolor backcolor | hr link | alignleft aligncenter alignright alignjustify | numlist bullist outdent indent | table | emoticons | removeformat | help',
                                    toolbar_mode: 'sliding',
                                }}
                            />
                        </div>
                        {errors && errors.message && (
                            <Typography color="error" variant="caption">
                                {errors.message}
                            </Typography>
                        )}
                    </FormControl>
                </>
            </AppDialog>
        </div>
    )
}

export default ComposeMessage
